import { Plane, Raycaster, Vector2, Vector3 } from 'three';
import { clampZoom } from '../../domain/camera.js';
import {
  DEFAULT_KEY_REMAPS,
  keyMatchesAction
} from '../../domain/input/defaultKeyRemaps.js';
import { CameraController } from './cameraController.js';
import { clickTargetVisible } from './clickTeamVision.js';

export class InputController {
  constructor({
    domElement,
    camera,
    groundPlane,
    championEntity,
    getDamageableTargetsCallback,
    getActiveJungleMonstersCallback,
    targetMarkerMesh,
    mapBounds,
    bushZones,
    getActiveWardsCallback,
    wallBoxes,
    onActivateQ,
    onCastW,
    onCastE,
    onCastR,
    onToggleShop,
    onInspectEntity,
    onRecallAttempt,
    onCastD,
    onCastF,
    remaps
  }) {
    this.domElement = domElement;
    this.camera = camera;
    this.groundPlane = groundPlane || new Plane(new Vector3(0, 1, 0), 0);
    this.championEntity = championEntity;
    this.getDamageableTargetsCallback = getDamageableTargetsCallback;
    this.getActiveJungleMonstersCallback = getActiveJungleMonstersCallback;
    this.targetMarkerMesh = targetMarkerMesh;
    this.mapBounds = mapBounds;
    this.bushZones = bushZones || [];
    this.wallBoxes = wallBoxes || [];
    this.getActiveWardsCallback = getActiveWardsCallback || (() => []);
    this.onActivateQ = onActivateQ;
    this.onCastW = onCastW;
    this.onCastE = onCastE;
    this.onCastR = onCastR;
    this.onToggleShop = onToggleShop;
    this.onInspectEntity = onInspectEntity || null;
    this.onRecallAttempt = onRecallAttempt || null;
    this.onCastD = onCastD || null;
    this.onCastF = onCastF || null;
    this.remaps = { ...(remaps || DEFAULT_KEY_REMAPS) };

    this.zoomLevel = 1.0;
    this.inputFrozen = false;
    this.pressedKeys = {};
    this.cameraController = new CameraController({
      locked: true,
      x: championEntity ? championEntity.x : 0,
      z: championEntity ? championEntity.z : 0
    });

    this.raycaster = new Raycaster();
    this.mouseVec = new Vector2();
    this.intersectionPoint = new Vector3();
    this.currentMouseWorld = new Vector3(0, 0, 0);
  }

  init() {
    this.bindMouseMove();
    this.bindClick();
    this.bindContextMenu();
    this.bindKeydown();
    this.bindWheel();
  }

  setChampionEntity(newChampion) {
    this.championEntity = newChampion;
    if (newChampion && this.cameraController.locked) {
      this.cameraController.snapTo(newChampion);
    }
  }

  /** P3-15: block move/cast/shop/recall hotkeys after match end. */
  setInputFrozen(frozen) {
    this.inputFrozen = !!frozen;
    if (this.inputFrozen) this.pressedKeys = {};
  }

  isInputFrozen() {
    return this.inputFrozen === true;
  }

  bindMouseMove() {
    this.domElement.addEventListener('mousemove', (e) => {
      const rect = this.domElement.getBoundingClientRect();
      const localX = e.clientX - rect.left;
      const localY = e.clientY - rect.top;
      this.cameraController.setMouseScreen(localX, localY, rect.width, rect.height);

      this.mouseVec.x = (localX / rect.width) * 2 - 1;
      this.mouseVec.y = -(localY / rect.height) * 2 + 1;

      this.raycaster.setFromCamera(this.mouseVec, this.camera);
      if (this.raycaster.ray.intersectPlane(this.groundPlane, this.intersectionPoint)) {
        this.currentMouseWorld.copy(this.intersectionPoint);
      }
    });
  }

  bindClick() {
    this.domElement.addEventListener('click', (e) => {
      if (this.inputFrozen) return;
      const rect = this.domElement.getBoundingClientRect();
      this.mouseVec.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      this.mouseVec.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      this.raycaster.setFromCamera(this.mouseVec, this.camera);
      if (!this.raycaster.ray.intersectPlane(this.groundPlane, this.intersectionPoint)) return;

      const hitTarget = this.findClickHitTarget();
      if (this.onInspectEntity) this.onInspectEntity(hitTarget);

      if (hitTarget && hitTarget.team !== this.championEntity.team) {
        this.championEntity.selectTarget(hitTarget);
      } else {
        this.championEntity.selectTarget(null);
      }
    });
  }

  findClickHitTarget() {
    const targets = this.getDamageableTargetsCallback ? this.getDamageableTargetsCallback() : [];
    const jungle = this.getActiveJungleMonstersCallback ? this.getActiveJungleMonstersCallback() : [];
    const all = [...targets, ...jungle];
    const px = this.intersectionPoint.x;
    const pz = this.intersectionPoint.z;
    return all.find((t) => {
      if (!t || t.isDead) return false;
      const reach = (t.radius / 16) + 0.5;
      if (Math.hypot(t.x - px, t.z - pz) > reach) return false;
      if (!clickTargetVisible(this, t)) {
        return false;
      }
      return true;
    }) || null;
  }

  bindContextMenu() {
    this.domElement.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      if (this.inputFrozen) return;
      const rect = this.domElement.getBoundingClientRect();
      this.mouseVec.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      this.mouseVec.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      this.raycaster.setFromCamera(this.mouseVec, this.camera);
      if (this.raycaster.ray.intersectPlane(this.groundPlane, this.intersectionPoint)) {
        const targets = this.getDamageableTargetsCallback ? this.getDamageableTargetsCallback() : [];
        const jungleMonsters = this.getActiveJungleMonstersCallback ? this.getActiveJungleMonstersCallback() : [];
        const allTargets = [...targets, ...jungleMonsters];
        const hitTarget = allTargets.find(t => !t.isDead && clickTargetVisible(this, t) && Math.hypot(t.x - this.intersectionPoint.x, t.z - this.intersectionPoint.z) <= (t.radius / 16 + 0.6));

        if (hitTarget) {
          this.championEntity.issueAttackCommand(hitTarget);
          if (this.targetMarkerMesh) this.targetMarkerMesh.visible = false;
        } else {
          const [clampedX, clampedZ] = this.championEntity.clampToBounds ? this.championEntity.clampToBounds(this.intersectionPoint.x, this.intersectionPoint.z, this.mapBounds) : [this.intersectionPoint.x, this.intersectionPoint.z];
          this.championEntity.cancelAttackWithMovement(clampedX, clampedZ);

          if (this.targetMarkerMesh) {
            this.targetMarkerMesh.position.set(this.championEntity.targetX, 0.06, this.championEntity.targetZ);
            this.targetMarkerMesh.visible = true;
          }
        }
      }
    });
  }

  bindKeydown() {
    window.addEventListener('keydown', (e) => {
      const activeTag = document.activeElement ? document.activeElement.tagName.toLowerCase() : '';
      if (activeTag === 'input' || activeTag === 'textarea' || activeTag === 'select') return;
      this.dispatchHotkey(e.key, { repeat: e.repeat });
    });

    window.addEventListener('keyup', (e) => {
      this.pressedKeys[e.key.toLowerCase()] = false;
    });
  }

  setRemaps(remaps) {
    this.remaps = { ...(remaps || DEFAULT_KEY_REMAPS) };
  }

  /** Hotkeys for unit tests and keydown (AR-05 recall B). */
  dispatchHotkey(key, { repeat = false } = {}) {
    if (this.inputFrozen) return;
    const k = String(key || '').toLowerCase();
    const m = this.remaps;
    if (keyMatchesAction(key, 'abilityQ', m) && this.onActivateQ) this.onActivateQ();
    if (keyMatchesAction(key, 'abilityW', m) && this.onCastW) {
      this.onCastW(this.currentMouseWorld);
    }
    if (keyMatchesAction(key, 'abilityE', m) && this.onCastE) {
      this.onCastE(this.currentMouseWorld);
    }
    if (keyMatchesAction(key, 'abilityR', m) && this.onCastR) {
      this.onCastR(this.currentMouseWorld);
    }
    if (keyMatchesAction(key, 'shop', m) && this.onToggleShop) this.onToggleShop();
    if (k === 'b' && this.onRecallAttempt) this.onRecallAttempt(this.championEntity);
    if (keyMatchesAction(key, 'summonerD', m) && this.onCastD) this.onCastD();
    if (keyMatchesAction(key, 'summonerF', m) && this.onCastF) this.onCastF();
    if (k === 'y' && !repeat && this.championEntity) {
      this.cameraController.toggleLock(this.championEntity);
    }
    this.pressedKeys[k] = true;
  }

  bindWheel() {
    if (!this.domElement) return;
    this.domElement.addEventListener('wheel', (e) => {
      e.preventDefault();
      const zoomDelta = e.deltaY < 0 ? 0.1 : -0.1;
      this.zoomLevel = clampZoom(this.zoomLevel + zoomDelta, 0.5, 2.0);
      if (this.camera) {
        this.camera.zoom = this.zoomLevel;
        this.camera.updateProjectionMatrix();
      }
    }, { passive: false });
  }

  updateCamera(dtSeconds) {
    if (!this.championEntity) return this.cameraController.getCenter();
    this.cameraController.update(dtSeconds, this.championEntity, this.pressedKeys);
    return this.cameraController.getCenter();
  }
}
